import ModalBase, { IModalBaseProps, ModalBaseRefType } from "@/Components/Base/ModalBase";
import PessoaBloqueio from "@/Models/Pessoa/PessoaBloqueio";
import { CheckOutlined} from "@ant-design/icons";
import { Collapse, Divider, Form, FormProps, Input, notification, Typography } from "antd";
import BloqueioTipoSelect from "./BloqueioTipo/BloqueioTipoSelect";
import { usePessoaBloquear, usePessoaBloquioBuscar, usePessoaDesbloquear } from "@/Services/Pessoa/PessoaBloqueioService";
import { useEffect, useRef } from "react";
import { EnumBloqueioTipo } from "@/Models/Pessoa/Bloqueio/BloqueioTipo";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";
import TelaCarregamento from "@/Components/Base/Carregamento/TelaCarregamento";
import InputDate from "@/Components/Base/Input/InputDate";
import BloqueioItemTable from "./Item/BloqueioItemTable";

const { TextArea } = Input;
const { Text } = Typography;

export interface IPessoaBloqueioDesbloquearModalProps extends Omit<IModalBaseProps, "children"> {
    codigo : number;
    formProps ?: Omit<FormProps<PessoaBloqueio>, "children">;
    onSuccess ?: Parameters<typeof usePessoaDesbloquear>[0]["onSuccess"];
}

export default function PessoaBloqueioDesbloquearModal ({ 
    codigo,
    formProps,
    onSuccess,
    ...props
}:IPessoaBloqueioDesbloquearModalProps) {
    const component = formProps?.component ?? "div";
    const layout = formProps?.layout ?? "vertical";
    const autoComplete = formProps?.autoComplete ?? "off";

    const [form] = Form.useForm<PessoaBloqueio>();

    const refModal = useRef<ModalBaseRefType|null>(null);

    const {
        mutate,
        isPending
    } = usePessoaDesbloquear({
        onSuccess(data, variables, context) {
            onSuccess?.(data, variables, context);
            refModal?.current?.fechar();
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso("Bloqueio salvo com sucesso.")
            })
        }
    });

    const {
        data,
        isLoading,
        refetch
    } = usePessoaBloquioBuscar({
        codigo : codigo,
        bloqueado : undefined,
        ativo : true,
        enabled : false
    });

    const desbloqueado = !!data?.dataLiberacao;

    useEffect(() => form.resetFields(), []);

    useEffect(() => {
        if (data)
        {
            form.setFieldsValue(data);
        }
    }, [data]);

    return <ModalBase
        ref={refModal}
        title="Desbloquear"
        textButtonOpen={undefined}
        okText="Desbloquear"
        propsButtonOpen={{
            icon : <CheckOutlined />
        }}
        width={"65vw"}
        confirmLoading={isPending || isLoading}
        destroyOnClose={true}
        okButtonProps={desbloqueado || data?.codBloqueioTipo == EnumBloqueioTipo.Inadimplencia ? {
            style : { display : "none" }
        } : undefined}
        cancelText={desbloqueado || data?.codBloqueioTipo == EnumBloqueioTipo.Inadimplencia ? "Fechar" : "Cancelar"}
        onOk={() => form.submit()}
        onClose={() => form.resetFields()}
        onOpen={() => {
            refetch();
        }}
        {...props}
    >
        <TelaCarregamento ativo={isLoading}>
            <Form
                {...formProps}
                component={component}
                layout={layout}
                autoComplete={autoComplete} 
                form={form}
                onFinish={(values) => {
                    values.codigo = codigo;
                    mutate(values);
                }}
            >
                <Form.Item<PessoaBloqueio> name="codigo" hidden label="Código">    
                    <input />
                </Form.Item>

                <Form.Item<PessoaBloqueio> name="codPessoa" hidden label="Código">    
                    <input />
                </Form.Item>

                <Divider style={{ marginTop : 10, marginBottom : 10 }}>Informações do bloqueio</Divider>

                <Form.Item<PessoaBloqueio> name="dataBloqueio" label="Data Bloqueio">    
                    <InputDate
                        disabled
                        showTime
                        placeholder="Data Bloqueio"
                    />
                </Form.Item>

                <Form.Item<PessoaBloqueio> name="motivo" label="Motivo bloqueio">    
                    <TextArea
                        disabled
                        placeholder="Motivo bloqueio"
                        autoSize={{ minRows: 3, maxRows: 5 }}
                    />
                </Form.Item>

                {data && <Collapse
                    size="small"
                    items={[
                        {
                            key : '1',
                            label : 'Pendências',
                            children : <BloqueioItemTable
                                codBloqueio={codigo} 
                                codBloqueioTipo={data.codBloqueioTipo == EnumBloqueioTipo.Manual ? "Manual" : "Inadimplencia"} 
                            />
                        }
                    ]}    
                />}

                <Divider style={{ marginTop : 10, marginBottom : 10 }}>Informações do desbloqueio</Divider>

                {desbloqueado && <Form.Item<PessoaBloqueio> name="dataLiberacao" label="Data desbloqueio">    
                    <InputDate
                        disabled
                        placeholder="Data desbloqueio"
                        showTime
                    />
                </Form.Item>}

                <Form.Item<PessoaBloqueio> name="motivoLiberacao" label="Motivo liberação" rules={[{ max : 1000 }]} >    
                    <TextArea
                        disabled={desbloqueado}
                        placeholder={desbloqueado ? undefined : "Motivo liberação"}
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        maxLength={1000}
                    />
                </Form.Item>
            </Form>
        </TelaCarregamento>
    </ModalBase>;
}

